/**
 * Decode a JWT payload without verification. Returns null on malformed
 * tokens. Use only for reading non-security-sensitive claims (sub, exp);
 * never use this to make a trust decision — the signature is not checked.
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    return JSON.parse(atob(parts[1]));
  } catch {
    return null;
  }
}

/** Pull the `sub` claim out of a JWT. Returns null if absent/malformed. */
export function decodeJwtSub(token: string): string | null {
  const payload = decodeJwtPayload(token);
  return typeof payload?.['sub'] === 'string' ? (payload['sub'] as string) : null;
}

/**
 * Pull the `exp` claim out of a JWT and convert to a Date.
 * Returns null if absent/malformed. JWT exp is seconds since epoch.
 */
export function decodeJwtExp(token: string): Date | null {
  const payload = decodeJwtPayload(token);
  const exp = payload?.['exp'];
  return typeof exp === 'number' ? new Date(exp * 1000) : null;
}
